import { memo, useState } from "react";
import {
  MiButton,
  MiDatePicker,
  MiIcon,
  MiStackedTypography,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  PERMISSIONS,
} from "@miplatform/constants";
import {
  addToDate,
  formatDate,
  getDate,
  subtractFromDate,
} from "@miplatform/utils";
import { mdiChevronLeft, mdiChevronRight, mdiThumbUpOutline } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { ORDERS_DEFAULT_FIELD_WIDTH } from "../../ordersConstants";
import { OrdersListActionMenu } from "../ActionMenu/OrdersListActionMenu";
import { useScheduledOrderActionsViewModel } from "./useScheduledOrderActionsViewModel";

/**
 * `ScheduledOrderActions` displays and manages actions related to a scheduled order,
 * including confirming the order and viewing additional options.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.id - Unique identifier for the scheduled order
 * @param {string|Date} props.approvedStartDate - The approved start date of the order
 * @param {string|Date} props.modifiedOn - The last modification date of the order
 * @param {Function} props.onRefresh - Callback to refresh data after actions are performed
 *
 * @returns {JSX.Element} A component with date display, confirm button, and action menu
 */
export const ScheduledOrderActions = memo((props) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();

  const canConfirmOrder = hasPermission(PERMISSIONS.ORDER.CONFIRM);

  const { id, approvedStartDate, modifiedOn, onRefresh } = props;
  const [confirmedScheduleDate, setConfirmedScheduleDate] = useState(
    getDate(approvedStartDate)
  );
  const { isLoading, confirmOrder } = useScheduledOrderActionsViewModel({
    id,
    modifiedOn,
    onRefresh,
  });
  const DATE_PICKER_WIDTH = 225;

  const handleResetConfirmDate = () =>
    setConfirmedScheduleDate(getDate(approvedStartDate));

  const handleDecreaseConfirmDate = () =>
    setConfirmedScheduleDate(subtractFromDate(confirmedScheduleDate, 1, "days"));

  const handleIncreaseConfirmDate = () =>
    setConfirmedScheduleDate(addToDate(confirmedScheduleDate, 1, "days"));

  const handleDateChange = (value) => {
    setConfirmedScheduleDate(value);
  };

  const handleConfirm = () => {
    confirmOrder(
      formatDate(
        confirmedScheduleDate,
        DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
      )
    );
  };

  return (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
      <MiStackedTypography
        title="Approved Date"
        titleProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
        description={
          approvedStartDate
            ? formatDate(approvedStartDate, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)
            : "--"
        }
        descriptionProps={{ width: ORDERS_DEFAULT_FIELD_WIDTH }}
      />
      <Box width={DATE_PICKER_WIDTH}>
        <MiDatePicker
          label="Confirmed Schedule Date"
          variant={DATEPICKER_VARIANTS.INLINE}
          actions={[DATEPICKER_ACTIONS.RESET, DATEPICKER_ACTIONS.CANCEL]}
          value={confirmedScheduleDate}
          onReset={handleResetConfirmDate}
          onChange={handleDateChange}
          startAdornment={
            <MiIcon
              path={mdiChevronLeft}
              size={1.2}
              color={theme.palette.grey.main}
              style={{
                cursor: "pointer",
                marginTop: "auto",
                marginBottom: "auto",
              }}
              role="button"
              onClick={handleDecreaseConfirmDate}
            />
          }
          endAdornment={
            <MiIcon
              path={mdiChevronRight}
              size={1.2}
              color={theme.palette.grey.main}
              style={{
                cursor: "pointer",
                marginTop: "auto",
                marginBottom: "auto",
              }}
              role="button"
              onClick={handleIncreaseConfirmDate}
            />
          }
        />
      </Box>
      {canConfirmOrder && (
        <MiButton
          title="Confirm"
          variant={BUTTON_VARIANTS.OUTLINED}
          disabled={isLoading}
          startIcon={{
            path: mdiThumbUpOutline,
            iconSize: 0.8,
            iconColor: theme.palette.primary.main,
          }}
          onClick={handleConfirm}
        />
      )}
      <OrdersListActionMenu
        menuViewConfig={{
          Reschedule: true,
          Hold: true,
          Cancel: true,
          OrderActivityDetails: true,
          ForceComplete: true,
          Push24h: false,
          UndoApprove: true,
        }}
        {...props}
      />
    </Box>
  );
});
